import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  FileText, Clock, CheckCircle, AlertCircle, LogOut, BookOpen, Plus, ArrowRight, Shield,
  Upload, Download, Trash2, Edit, Eye, X, Key, FolderOpen, RefreshCw
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import PendingApproval from './PendingApproval';
import { toast } from 'sonner';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '', description: '', status: 'Scheduled', progress: 0,
    start_date: '', estimated_completion: '', notes: ''
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadData = useCallback(async () => {
    try {
      const [projectsRes, documentsRes, caseStudiesRes] = await Promise.all([
        fetch(`${API_URL}/api/projects`, { credentials: 'include' }),
        fetch(`${API_URL}/api/documents`, { credentials: 'include' }),
        fetch(`${API_URL}/api/case-studies`, { credentials: 'include' })
      ]);

      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (documentsRes.ok) setDocuments(await documentsRes.json());
      if (caseStudiesRes.ok) setCaseStudies(await caseStudiesRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      loadData();
      return;
    }

    const checkAuth = async () => {
      try {
        // Build headers - use saved token if available (Remember Me)
        const headers = {};
        const savedToken = localStorage.getItem('bcs_session_token');
        if (savedToken) {
          headers['Authorization'] = `Bearer ${savedToken}`;
        }
        const response = await fetch(`${API_URL}/api/auth/me`, { 
          credentials: 'include',
          headers 
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('isAuthenticated', 'true');
        loadData();
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('bcs_session_token');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate, location.state, API_URL, loadData]);

  const handleLogout = async () => {
    try {
      const headers = {};
      const savedToken = localStorage.getItem('bcs_session_token');
      if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include', headers });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('bcs_session_token');
    navigate('/');
  };

  // Project handlers
  const handleSaveProject = async () => {
    if (!projectForm.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setActionLoading(true);
    try {
      const url = editingProject 
        ? `${API_URL}/api/projects/${editingProject.project_id}`
        : `${API_URL}/api/projects`;
      const method = editingProject ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(projectForm)
      });

      if (response.ok) {
        toast.success(editingProject ? 'Project updated' : 'Project created');
        setShowProjectModal(false);
        setEditingProject(null);
        setProjectForm({ name: '', description: '', status: 'Scheduled', progress: 0, start_date: '', estimated_completion: '', notes: '' });
        loadData();
      } else {
        toast.error('Failed to save project');
      }
    } catch (error) {
      toast.error('Error saving project');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Project deleted');
        loadData();
      }
    } catch (error) {
      toast.error('Error deleting project');
    }
  };

  // Document handlers
  const handleUploadDocument = async () => {
    if (!documentFile || !documentName.trim()) {
      toast.error('Please select a file and enter a name');
      return;
    }
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('name', documentName);
      formData.append('description', documentDescription);

      const response = await fetch(`${API_URL}/api/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success('Document uploaded');
        setShowDocumentModal(false);
        setDocumentFile(null);
        setDocumentName('');
        setDocumentDescription('');
        loadData();
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('Error uploading document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${docId}/download`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Error downloading document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const response = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Document deleted');
        loadData();
      }
    } catch (error) {
      toast.error('Error deleting document');
    }
  };

  // Password change handler
  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.new
        })
      });

      if (response.ok) {
        toast.success('Password changed successfully');
        setShowPasswordModal(false);
        setPasswordForm({ current: '', new: '', confirm: '' });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Error changing password');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircle className="text-green-600" size={20} />;
      case 'In Progress': return <Clock className="text-blue-600" size={20} />;
      case 'Scheduled': return <AlertCircle className="text-yellow-600" size={20} />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Scheduled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (user && !user.is_approved) {
    return <PendingApproval user={user} onLogout={handleLogout} />;
  }

  const isAdmin = user?.email === 'jasonkraft@bcs-us.com';
  const activeProjects = projects.filter(p => p.status !== 'Completed').length;
  const completedProjects = projects.filter(p => p.status === 'Completed').length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Dashboard Header */}
      <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">
                Welcome back, {user?.name || 'Customer'}!
              </h1>
              <p className="text-xl text-gray-600">Your BCS Customer Portal</p>
              {user?.email && <p className="text-sm text-gray-500 mt-1">{user.email}</p>}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => setShowPasswordModal(true)} data-testid="change-password-btn">
                <Key size={18} className="mr-2" />
                Change Password
              </Button>
              {isAdmin && (
                <Button onClick={() => navigate('/admin')} className="bg-amber-600 hover:bg-amber-700 flex items-center gap-2">
                  <Shield size={18} />
                  Admin Panel
                </Button>
              )}
              <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
                <LogOut size={18} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-8">
          
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-900">{projects.length}</p>
                  <p className="text-sm text-gray-600 mt-1">Total Projects</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{completedProjects}</p>
                  <p className="text-sm text-gray-600 mt-1">Completed</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-600">{activeProjects}</p>
                  <p className="text-sm text-gray-600 mt-1">Active</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-900">{documents.length}</p>
                  <p className="text-sm text-gray-600 mt-1">Documents</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Projects Section */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Your Projects</CardTitle>
                  <CardDescription>Track the status of your ongoing and completed projects</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadData}>
                    <RefreshCw size={16} />
                  </Button>
                  <Button 
                    className="bg-blue-900 hover:bg-blue-800"
                    onClick={() => { setEditingProject(null); setProjectForm({ name: '', description: '', status: 'Scheduled', progress: 0, start_date: '', estimated_completion: '', notes: '' }); setShowProjectModal(true); }}
                    data-testid="add-project-btn"
                  >
                    <Plus size={18} className="mr-2" />
                    Add Project
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FolderOpen className="mx-auto mb-2" size={32} />
                  <p>No projects yet. Add your first project!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div key={project.project_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(project.status)}
                          <div>
                            <h3 className="font-semibold text-slate-900">{project.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {project.status === 'Completed' && project.completed_date && `Completed: ${project.completed_date}`}
                              {project.status === 'In Progress' && project.estimated_completion && `Est. Completion: ${project.estimated_completion}`}
                              {project.status === 'Scheduled' && project.start_date && `Scheduled: ${project.start_date}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(project.status)}>{project.status}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingProject(project); setProjectForm(project); setShowProjectModal(true); }}>
                            <Edit size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteProject(project.project_id)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                      {project.status === 'In Progress' && (
                        <div className="mt-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Progress</span>
                            <span className="font-medium">{project.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-900 h-2 rounded-full transition-all" style={{ width: `${project.progress}%` }} />
                          </div>
                        </div>
                      )}
                      {project.description && <p className="text-sm text-gray-600 mt-2">{project.description}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Case Studies Section */}
          <Card className="border-2 border-blue-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="text-blue-900" size={24} />
                    Case Studies
                  </CardTitle>
                  <CardDescription className="mt-1">Document and showcase your repair application projects</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => navigate('/customer-portal/case-studies')} data-testid="view-all-case-studies-btn">
                    View All ({caseStudies.length})
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                  <Button className="bg-blue-900 hover:bg-blue-800" onClick={() => navigate('/customer-portal/case-studies/create')} data-testid="create-new-case-study-btn">
                    <Plus size={18} className="mr-2" />
                    Create New
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Documents Section */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Documents & Reports</CardTitle>
                  <CardDescription>Access your project documentation, reports, and invoices</CardDescription>
                </div>
                <Button className="bg-blue-900 hover:bg-blue-800" onClick={() => setShowDocumentModal(true)} data-testid="upload-document-btn">
                  <Upload size={18} className="mr-2" />
                  Upload Document
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="mx-auto mb-2" size={32} />
                  <p>No documents yet. Upload your first document!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileText className="text-blue-900" size={24} />
                        <div>
                          <p className="font-medium text-slate-900">{doc.name}</p>
                          <p className="text-sm text-gray-600">{doc.document_type} • {(doc.size / 1024).toFixed(1)} KB • {new Date(doc.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleDownloadDocument(doc.document_id, doc.original_filename)}>
                          <Download size={16} className="mr-1" />
                          Download
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteDocument(doc.document_id)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Need Assistance?</h3>
                <p className="text-gray-600 mb-4">Our team is here to help with any questions about your projects.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button className="bg-blue-900 hover:bg-blue-800">
                    <a href="tel:+19493719948">Call (949) 371-9948</a>
                  </Button>
                  <Button variant="outline">
                    <a href="mailto:info@bcs-us.com">Email Support</a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Project Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowProjectModal(false)}><X size={20} /></Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Project Name *</Label>
                  <Input value={projectForm.name} onChange={(e) => setProjectForm({...projectForm, name: e.target.value})} placeholder="e.g., Cooling Tower Repair" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={projectForm.description || ''} onChange={(e) => setProjectForm({...projectForm, description: e.target.value})} placeholder="Project description..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <select className="w-full border rounded-md p-2" value={projectForm.status} onChange={(e) => setProjectForm({...projectForm, status: e.target.value})}>
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>
                  <div>
                    <Label>Progress (%)</Label>
                    <Input type="number" min="0" max="100" value={projectForm.progress} onChange={(e) => setProjectForm({...projectForm, progress: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <Input type="date" value={projectForm.start_date || ''} onChange={(e) => setProjectForm({...projectForm, start_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Est. Completion</Label>
                    <Input type="date" value={projectForm.estimated_completion || ''} onChange={(e) => setProjectForm({...projectForm, estimated_completion: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={projectForm.notes || ''} onChange={(e) => setProjectForm({...projectForm, notes: e.target.value})} placeholder="Additional notes..." />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowProjectModal(false)}>Cancel</Button>
                  <Button className="bg-blue-900 hover:bg-blue-800" onClick={handleSaveProject} disabled={actionLoading}>
                    {actionLoading ? 'Saving...' : 'Save Project'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Upload Document</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowDocumentModal(false)}><X size={20} /></Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Document Name *</Label>
                  <Input value={documentName} onChange={(e) => setDocumentName(e.target.value)} placeholder="e.g., Invoice #2024-001" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={documentDescription} onChange={(e) => setDocumentDescription(e.target.value)} placeholder="Brief description..." />
                </div>
                <div>
                  <Label>File *</Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <input type="file" onChange={(e) => setDocumentFile(e.target.files[0])} className="w-full" />
                    {documentFile && <p className="text-sm text-green-600 mt-2">Selected: {documentFile.name}</p>}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowDocumentModal(false)}>Cancel</Button>
                  <Button className="bg-blue-900 hover:bg-blue-800" onClick={handleUploadDocument} disabled={actionLoading}>
                    {actionLoading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Change Password</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowPasswordModal(false)}><X size={20} /></Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Current Password</Label>
                  <Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})} />
                </div>
                <div>
                  <Label>New Password</Label>
                  <Input type="password" value={passwordForm.new} onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})} />
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})} />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
                  <Button className="bg-blue-900 hover:bg-blue-800" onClick={handleChangePassword} disabled={actionLoading}>
                    {actionLoading ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CustomerDashboard;
